import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Theme } from '../../../styles/theme';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'ghost'
    | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  children?: ReactNode;
}

const getVariantStyles = (
  variant: ButtonProps['variant'],
  theme: Theme,
): any => {
  const variants = {
    primary: css`
      background-color: ${theme.colors.primary.main};
      color: ${theme.colors.primary.contrastText};
      border: 1px solid ${theme.colors.primary.main};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.primary.dark};
        border-color: ${theme.colors.primary.dark};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.primary.dark};
        transform: translateY(1px);
      }
    `,
    secondary: css`
      background-color: ${theme.colors.background.paper};
      color: ${theme.colors.primary.main};
      border: 1px solid ${theme.colors.primary.main};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.primary.main}10;
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.primary.main}20;
        transform: translateY(1px);
      }
    `,
    success: css`
      background-color: ${theme.colors.success.main};
      color: ${theme.colors.success.contrastText};
      border: 1px solid ${theme.colors.success.main};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.success.dark};
        border-color: ${theme.colors.success.dark};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.success.dark};
        transform: translateY(1px);
      }
    `,
    danger: css`
      background-color: ${theme.colors.error.main};
      color: ${theme.colors.error.contrastText};
      border: 1px solid ${theme.colors.error.main};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.error.dark};
        border-color: ${theme.colors.error.dark};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.error.dark};
        transform: translateY(1px);
      }
    `,
    warning: css`
      background-color: ${theme.colors.warning.main};
      color: ${theme.colors.warning.contrastText};
      border: 1px solid ${theme.colors.warning.main};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.warning.dark};
        border-color: ${theme.colors.warning.dark};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.warning.dark};
        transform: translateY(1px);
      }
    `,
    ghost: css`
      background-color: transparent;
      color: ${theme.colors.text.primary};
      border: 1px solid
        ${theme.mode === 'dark'
          ? theme.colors.neutral[600]
          : theme.colors.divider};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.action.hover};
        border-color: ${theme.mode === 'dark'
          ? theme.colors.neutral[500]
          : theme.colors.neutral[600]};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.action.selected};
        transform: translateY(1px);
      }
    `,
    text: css`
      background-color: transparent;
      color: ${theme.colors.primary.main};
      border: 1px solid transparent;

      &:hover:not(:disabled) {
        background-color: ${theme.colors.action.hover};
      }

      &:active:not(:disabled) {
        background-color: ${theme.colors.action.selected};
        transform: translateY(1px);
      }
    `,
  };

  return variants[variant || 'primary'];
};

const getSizeStyles = (size: ButtonProps['size'], theme: Theme): any => {
  const sizes = {
    small: css`
      padding: ${theme.spacing[1.5]} ${theme.spacing[3]};
      font-size: ${theme.typography.fontSize.sm};
      border-radius: ${theme.borderRadius.md};
      min-height: 32px;
    `,
    medium: css`
      padding: ${theme.spacing[2]} ${theme.spacing[4]};
      font-size: ${theme.typography.fontSize.base};
      border-radius: ${theme.borderRadius.lg};
      min-height: 40px;
    `,
    large: css`
      padding: ${theme.spacing[3]} ${theme.spacing[5]};
      font-size: ${theme.typography.fontSize.lg};
      border-radius: ${theme.borderRadius.lg};
      min-height: 48px;
    `,
  };

  return sizes[size || 'medium'];
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-family: ${({ theme }) => theme.typography.button.fontFamily};
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  line-height: ${({ theme }) => theme.typography.button.lineHeight};
  letter-spacing: ${({ theme }) => theme.typography.button.letterSpacing};
  text-transform: ${({ theme }) => theme.typography.button.textTransform};
  white-space: nowrap;
  user-select: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.duration.fast}
    ${({ theme }) => theme.transitions.easing.inOut};
  position: relative;
  overflow: hidden;

  ${({ variant, theme }) => getVariantStyles(variant, theme)}
  ${({ size, theme }) => getSizeStyles(size, theme)}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  ${({ loading }) =>
    loading &&
    css`
      color: transparent;
      pointer-events: none;
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 2px;
  }

  svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span<{ position?: 'left' | 'right' }>`
  display: inline-flex;
  align-items: center;
  order: ${({ position }) => (position === 'right' ? 1 : -1)};
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  disabled,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      loading={loading}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {icon && !loading && (
        <IconWrapper position={iconPosition}>{icon}</IconWrapper>
      )}
      {children}
    </StyledButton>
  );
};

export default Button;
